import React from 'react';
import { BMP_DepositGroup } from '../../../models/bmp.model';
import { formatCurrency } from '../utils/formatCurrency';
import Decimal from 'decimal.js';

interface Props {
    group: BMP_DepositGroup | null;
    onClose: () => void;
}

// Helper to safely format values to 4 decimal places
const formatUnit = (val: Decimal | string | null | undefined, decimal: number) => {
    if (!val) return '0.0000';
    try {
        return new Decimal(val).toFixed(decimal);
    } catch {
        return '0.0000';
    }
};

// Helper component for ratio badges to keep code DRY and clean
const RatioBadge: React.FC<{ label: string; value: Decimal | string | null | undefined; color?: 'amber' | 'blue' | 'indigo' }> = ({ label, value, color = 'amber' }) => {
    const colorClasses = {
        amber: 'bg-amber-50 text-amber-800 border-amber-200/60',
        blue: 'bg-blue-50 text-blue-800 border-blue-200/60',
        indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200/60'
    };

    return (
        <div className={`flex flex-col px-3 py-2 rounded-xl border ${colorClasses[color]} transition-all hover:shadow-sm`}>
            <span className="text-[10px] font-black uppercase tracking-wider opacity-70 mb-0.5">{label}</span>
            <span className="font-mono text-sm font-bold">{formatUnit(value, 8)}</span>
        </div>
    );
};

export const GroupParticipantsModal: React.FC<Props> = ({ group, onClose }) => {
    if (!group) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            {/* Main Container */}
            <div className="bg-white w-full max-w-[95vw] lg:max-w-7xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                    <div>
                        <h3 className="text-xl font-black text-slate-900">Résztvevők listája</h3>
                        <p className="text-xs font-bold text-amber-600 uppercase tracking-tighter mt-0.5">
                            Group #{group.id} • {new Date(group.date).toLocaleDateString('hu-HU')}
                        </p>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors text-xl font-medium"
                    >
                        &times;
                    </button>
                </div>

                {group.BMP_GroupWallet && (
                    <div className="p-5 bg-slate-50/30 border-b border-slate-100 shrink-0">
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Jelenlegi egyenlegek a csoportban</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                            <RatioBadge label="Fix BTC" value={group.BMP_GroupWallet.fixBtc} color="amber" />
                            <RatioBadge label="Fix ETH" value={group.BMP_GroupWallet.fixEth} color="amber" />
                            <RatioBadge label="Robot BTC" value={group.BMP_GroupWallet.robotBtc} color="amber" />
                            <RatioBadge label="Robot Likv." value={group.BMP_GroupWallet.robotLiquidity} color="amber" />
                            <RatioBadge label="Trading BTC" value={group.BMP_GroupWallet.tradingBtc} color="amber" />
                            <RatioBadge label="Trading ETH" value={group.BMP_GroupWallet.tradingEth} color="amber" />
                            <RatioBadge label="Trading Likv." value={group.BMP_GroupWallet.tradingLiquidity} color="amber" />
                        </div>
                    </div>
                )}
                

                {/* Table Container */}
                <div className="overflow-auto flex-1">
                    <table className="w-full text-left whitespace-nowrap">
                        <thead className="sticky top-0 bg-white shadow-sm z-10">
                            <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                <th className="px-6 py-4">Név / Email</th>
                                <th className="px-6 py-4 text-right">Befizetés</th>
                                <th className="px-6 py-4 text-right bg-amber-50/40 text-amber-900 border-l border-amber-100/30">Total Units</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {group.participants?.map((p) => (
                                <tr key={p.id} className="text-sm hover:bg-slate-50/40 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-slate-800">{p.user?.name || 'N/A'}</div>
                                        <div className="text-xs text-slate-400 mt-0.5">{p.user?.email}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right font-semibold text-slate-700">
                                        {formatCurrency(p.depositUsd ? new Decimal(p.depositUsd).toNumber() : 0)}
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-amber-700 font-bold bg-amber-50/10 border-l border-amber-100/20">
                                        {formatUnit(p.units, 8)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {(!group.participants || group.participants.length === 0) && (
                        <div className="py-20 text-center text-slate-400 text-sm italic">
                            Nincsenek résztvevők rögzítve ehhez a csoporthoz.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 bg-slate-50 flex justify-end shrink-0 border-t border-slate-100">
                    <button 
                        onClick={onClose}
                        className="px-8 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100 hover:border-slate-300 active:bg-slate-200/70 transition-all shadow-sm"
                    >
                        Vissza
                    </button>
                </div>
            </div>
        </div>
    );
};